/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Ban, Crown, Key, Pencil, Plus, RefreshCw, Save, Scale, Trash2, Users, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { cx } from '../theme/cx';
import { MASCOTS, MascotAvatar } from './mascotCatalog';
import {
  Badge, Banner, Button, Card, CardHeader, Checkbox, Field, IconButton, Input, RadioMark, Select, SkeletonList, Textarea, useConfirm, useRadioGroup,
} from './ui';

const HORIZONS = [
  { value: 'short', label: 'Court terme (quelques mois)' },
  { value: 'medium', label: 'Moyen terme (1 à 3 ans)' },
  { value: 'long', label: 'Long terme (plusieurs années)' },
];

const mandateForm = (m) => (m ? { cash_min: String(m.cash_min).replace('.', ','), cash_max: String(m.cash_max).replace('.', ','), horizon: m.horizon } : null);
const decimal = (v) => Number(String(v).trim().replace(',', '.'));

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', keyHint: 'platform.openai.com' },
  { id: 'gemini', name: 'Google Gemini', keyHint: 'ai.google.dev' },
];

const NO_MASCOT = { id: '', name: 'Aucune' };

const emptyForm = { name: '', provider: 'openai', model: '', api_key: '', focus: '', enabled: true, mascot_variant: '' };

const LABEL = 'mb-2 block text-sm font-medium text-fg-body';

const optionClass = (checked) => cx(
  'rounded-control border transition-colors duration-150',
  checked ? 'border-gold-500/50 bg-gold-950/40 text-fg shadow-[inset_0_0_0_1px_rgb(var(--c-gold-500)/0.2)]' : 'border-line bg-surface-2/40 text-fg-muted hover:border-line-strong hover:bg-surface-2 hover:text-fg',
);

const ProviderPicker = ({ value, onChange }) => {
  const itemProps = useRadioGroup({ options: PROVIDERS, value, onChange, getValue: (p) => p.id });
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Fournisseur">
      {PROVIDERS.map((p, index) => (
        <button key={p.id} {...itemProps(p, index)} className={cx(optionClass(value === p.id), 'flex h-11 items-center justify-between gap-2 px-3 text-left text-sm font-medium')}>
          {p.name}
          <RadioMark checked={value === p.id} className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
};

// Optional mascot giving the expert a face in the expert reports.
const MascotPicker = ({ value, onChange, name }) => {
  const options = [NO_MASCOT, ...MASCOTS];
  const itemProps = useRadioGroup({ options, value, onChange, getValue: (m) => m.id });
  return (
    <div>
      <span className={LABEL}>Mascotte <span className="font-normal text-fg-subtle">(facultatif)</span></span>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Mascotte">
        {options.map((m, index) => (
          <button key={m.id || 'none'} {...itemProps(m, index)} title={m.name} className={cx(optionClass(value === m.id), 'flex w-[4.75rem] flex-col items-center gap-1.5 p-1.5')}>
            {m.id ? (
              <MascotAvatar variant={m.id} name={name} size="lg" />
            ) : (
              <span aria-hidden="true" className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-line-strong bg-surface-2 text-fg-subtle"><Ban size={20} /></span>
            )}
            <span className={cx('text-center text-[11px] leading-tight', value === m.id ? 'text-fg' : 'text-fg-muted')}>{m.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const apiError = (err, fallback) => err?.response?.data?.error || fallback;

const ExpertForm = ({ expert, onCancel, onSaved }) => {
  const [form, setForm] = useState(expert ? { ...emptyForm, ...expert, api_key: '' } : emptyForm);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const providerChanged = expert && form.provider !== expert.provider;
  const canUseStoredKey = expert && !providerChanged;
  const fieldId = (name) => `expert-${name}-${expert?.id || 'new'}`;

  const loadModels = async () => {
    setModelsLoading(true);
    setModelsError('');
    try {
      const res = await api.post('/experts/models', {
        provider: form.provider,
        api_key: form.api_key,
        expert_id: canUseStoredKey ? expert.id : 0,
      });
      setModels(res.data.models || []);
    } catch (err) {
      setModels([]);
      setModelsError(apiError(err, 'Impossible de charger les modèles.'));
    } finally {
      setModelsLoading(false);
    }
  };

  // Editing with a stored key can list models immediately, so the dropdown is
  // populated (with the current model selected) as soon as the form opens.
  const autoLoaded = useRef(false);
  useEffect(() => {
    if (!autoLoaded.current && canUseStoredKey) {
      autoLoaded.current = true;
      loadModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = { name: form.name, provider: form.provider, model: form.model, api_key: form.api_key, focus: form.focus, enabled: form.enabled, mascot_variant: form.mascot_variant || '' };
    try {
      if (expert) await api.put(`/experts/${expert.id}`, payload);
      else await api.post('/experts', payload);
      onSaved(form.name, !expert);
    } catch (err) {
      setError(apiError(err, 'Impossible d’enregistrer l’expert.'));
      setSaving(false);
    }
  };

  const hint = PROVIDERS.find((p) => p.id === form.provider)?.keyHint;
  const label = expert ? `Modifier ${expert.name}` : 'Nouvel expert';

  return (
    <form onSubmit={submit} className="space-y-5 rounded-card border border-gold-500/35 bg-surface-2/40 p-4 shadow-[inset_0_1px_0_rgb(var(--c-gold-500)/0.18)] sm:p-5" aria-label={label}>
      <p className="eyebrow text-gold-400/90">{label}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nom" htmlFor={fieldId('name')}>
          <Input id={fieldId('name')} value={form.name} maxLength={80} required onChange={(e) => set({ name: e.target.value })} placeholder="ex. Stratège macro" />
        </Field>
        <div>
          <span className={LABEL}>Fournisseur</span>
          <ProviderPicker value={form.provider} onChange={(id) => { if (id !== form.provider) { set({ provider: id, model: '' }); setModels([]); } }} />
        </div>
      </div>

      <Field
        label="Clé API"
        htmlFor={fieldId('key')}
        hint={`${providerChanged ? 'Changer de fournisseur impose une nouvelle clé. ' : ''}Obtenez une clé sur ${hint}.`}
      >
        <Input
          id={fieldId('key')}
          type="password"
          autoComplete="off"
          leadingIcon={Key}
          value={form.api_key}
          onChange={(e) => set({ api_key: e.target.value })}
          placeholder={canUseStoredKey ? `Conserver la clé actuelle (${expert.api_key})` : 'Saisissez la clé API de cet expert'}
          required={!canUseStoredKey}
        />
      </Field>

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <label htmlFor={fieldId('model')} className="text-sm font-medium text-fg-body">Modèle</label>
          <Button variant="ghost" size="sm" leadingIcon={RefreshCw} loading={modelsLoading} onClick={loadModels} disabled={!form.api_key && !canUseStoredKey}>
            Charger les modèles
          </Button>
        </div>
        {models.length > 0 ? (
          <Select id={fieldId('model')} value={form.model} required onChange={(e) => set({ model: e.target.value })}>
            <option value="" disabled>Choisir un modèle…</option>
            {form.model && !models.some((m) => m.id === form.model) && (
              <option value={form.model}>{form.model} (actuel)</option>
            )}
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.displayName && m.displayName !== m.id ? `${m.displayName} (${m.id})` : m.id}</option>
            ))}
          </Select>
        ) : (
          <Input id={fieldId('model')} value={form.model} required onChange={(e) => set({ model: e.target.value })} placeholder={form.provider === 'openai' ? 'ex. gpt-4o' : 'ex. gemini-2.5-pro'} />
        )}
        {modelsError && <p className="mt-1.5 text-xs text-loss-300">{modelsError}</p>}
        {!modelsError && models.length > 0 && <p className="mt-1.5 text-xs text-fg-subtle">{models.length} modèles disponibles.</p>}
      </div>

      <Field label="Angle d’analyse" optional="facultatif" htmlFor={fieldId('focus')}>
        <Textarea id={fieldId('focus')} className="min-h-[76px]" maxLength={1000} value={form.focus} onChange={(e) => set({ focus: e.target.value })}
          placeholder="Angle à privilégier, par ex. value et dividendes, macro et taux, analyse technique…" />
      </Field>

      <MascotPicker value={form.mascot_variant || ''} name={form.name} onChange={(id) => set({ mascot_variant: id })} />

      <label className="flex cursor-pointer items-center gap-3 text-sm text-fg-body">
        <Checkbox switch checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        Participe au rapport quotidien
      </label>

      {error && <Banner tone="loss" role="alert">{error}</Banner>}
      <div className="flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:justify-end">
        <Button variant="secondary" leadingIcon={X} onClick={onCancel}>Annuler</Button>
        <Button type="submit" loading={saving} leadingIcon={Save}>{expert ? 'Enregistrer l’expert' : 'Ajouter l’expert'}</Button>
      </div>
    </form>
  );
};

const ExpertRow = ({ expert, locked, onEdit, onDelete }) => (
  <div className="flex items-start gap-3 rounded-control border border-line bg-surface-2/40 p-4 sm:gap-4" data-testid="expert-row">
    <MascotAvatar variant={expert.mascot_variant} name={expert.name} size="lg" />
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-center gap-2 font-medium text-fg">
        {expert.name}
        {expert.is_rapporteur && <Badge tone="gold" icon={Crown}>Rapporteur</Badge>}
        {!expert.enabled && <Badge tone="neutral">Désactivé</Badge>}
      </p>
      <p className="mt-1 break-words text-sm text-fg-muted">
        {PROVIDERS.find((p) => p.id === expert.provider)?.name || expert.provider} · {expert.model} · clé <span className="font-mono text-[13px] text-fg-subtle">{expert.api_key}</span>
      </p>
      {expert.focus && <p className="mt-1.5 line-clamp-3 whitespace-pre-line break-words text-sm leading-relaxed text-fg-subtle" title={expert.focus}>{expert.focus}</p>}
    </div>
    <div className="-mr-1 -mt-1 flex flex-shrink-0 items-center">
      <IconButton icon={Pencil} label={`Modifier ${expert.name}`} size="sm" tooltipSide="bottom-end" onClick={onEdit} disabled={locked} />
      <IconButton icon={Trash2} label={`Supprimer ${expert.name}`} size="sm" tooltipSide="bottom-end" variant="danger" onClick={onDelete} disabled={locked} />
    </div>
  </div>
);

const ExpertsSettings = () => {
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | expert id
  const [panel, setPanel] = useState({ debate_rounds: 1, rapporteur_id: 0, weighted_votes: false });
  const [panelState, setPanelState] = useState({ saving: false, error: '' });
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const res = await api.get('/experts');
      setData(res.data);
      setPanel({
        debate_rounds: res.data.debate_rounds || 1,
        rapporteur_id: res.data.experts.find((e) => e.is_rapporteur)?.id || 0,
        weighted_votes: Boolean(res.data.weighted_votes),
        mandate: mandateForm(res.data.mandate),
      });
    } catch (err) {
      setLoadError(apiError(err, 'Impossible de charger les experts.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (name, created) => {
    setEditing(null);
    toast.success(created ? 'Expert ajouté' : 'Expert enregistré', { description: name ? `${name}${created ? ' rejoint le panel.' : ' est à jour.'}` : undefined });
    load();
  };

  const remove = async (expert) => {
    const ok = await confirm({
      title: `Supprimer l’expert « ${expert.name} » ?`,
      message: 'Sa clé API sera effacée.',
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      tone: 'danger',
    });
    if (!ok) return;
    setActionError('');
    try {
      await api.delete(`/experts/${expert.id}`);
      toast.success('Expert supprimé', { description: `${expert.name} a quitté le panel.` });
      load();
    } catch (err) {
      setActionError(apiError(err, 'Impossible de supprimer l’expert.'));
    }
  };

  const savePanel = async () => {
    const mandate = panel.mandate && { cash_min: decimal(panel.mandate.cash_min), cash_max: decimal(panel.mandate.cash_max), horizon: panel.mandate.horizon };
    if (mandate && (Number.isNaN(mandate.cash_min) || Number.isNaN(mandate.cash_max))) {
      setPanelState({ saving: false, error: 'Saisissez les liquidités du mandat en nombres.' });
      return;
    }
    setPanelState({ saving: true, error: '' });
    try {
      await api.put('/experts/panel', {
        debate_rounds: Number(panel.debate_rounds),
        rapporteur_id: Number(panel.rapporteur_id),
        weighted_votes: panel.weighted_votes,
        ...(mandate ? { mandate } : {}),
      });
      setPanelState({ saving: false, error: '' });
      toast.success('Panel enregistré');
      load();
    } catch (err) {
      setPanelState({ saving: false, error: apiError(err, 'Impossible d’enregistrer le panel.') });
    }
  };

  if (loadError) {
    return (
      <Card>
        <Banner tone="loss" role="alert" action={<Button variant="secondary" size="sm" onClick={load}>Réessayer</Button>}>{loadError}</Banner>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card role="status">
        <span className="sr-only">Chargement des experts…</span>
        <SkeletonList items={2} />
      </Card>
    );
  }

  const { experts, min_experts: minExperts, max_experts: maxExperts, max_debate_rounds: maxRounds } = data;
  const enabledCount = experts.filter((e) => e.enabled).length;
  const rapporteur = experts.find((e) => e.id === Number(panel.rapporteur_id));
  const panelSize = Math.max(enabledCount, minExperts);
  const full = experts.length >= maxExperts;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          icon={Users}
          title="Panel d’experts"
          description="Chaque expert analyse la journée de façon indépendante ; leurs désaccords passent en table ronde, puis le panel signe un rapport commun."
          actions={<Badge tone={enabledCount >= minExperts ? 'gold' : 'neutral'} className="num">{experts.length}/{maxExperts} experts</Badge>}
        />

        <div className="space-y-4">
          {enabledCount < minExperts && (
            <Banner tone="warn" data-testid="experts-minimum">
              Le rapport d’experts nécessite au moins {minExperts} experts actifs ({enabledCount} actuellement). En attendant, le rapport quotidien actuel continue d’être généré et envoyé par e-mail.
            </Banner>
          )}
          {actionError && <Banner tone="loss" role="alert">{actionError}</Banner>}

          <div className="space-y-3">
            {experts.length === 0 && editing !== 'new' && (
              <p className="rounded-control border border-dashed border-line-strong px-4 py-6 text-center text-sm text-fg-muted">Aucun expert pour le moment.</p>
            )}
            {experts.map((expert) => (editing === expert.id ? (
              <ExpertForm key={expert.id} expert={expert} onCancel={() => setEditing(null)} onSaved={onSaved} />
            ) : (
              <ExpertRow key={expert.id} expert={expert} locked={editing !== null} onEdit={() => setEditing(expert.id)} onDelete={() => remove(expert)} />
            )))}
            {editing === 'new' && <ExpertForm onCancel={() => setEditing(null)} onSaved={onSaved} />}
          </div>
        </div>

        {editing === null && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button variant="secondary" leadingIcon={Plus} onClick={() => setEditing('new')} disabled={full}>Ajouter un expert</Button>
            {full && <span className="text-sm text-fg-subtle">Nombre maximal d’experts atteint ({maxExperts}).</span>}
          </div>
        )}
      </Card>

      <Card>
        <CardHeader
          icon={Scale}
          title="Table ronde et consolidation"
          description="Les positions sont comparées ligne par ligne : les accords sont actés d’office, les points de friction passent en table ronde modérée par le rapporteur. Celui-ci rédige ensuite le rapport commun, que chaque autre expert approuve ou assortit de réserves. Les désaccords persistants restent visibles dans le rapport final."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Field label="Rapporteur" htmlFor="experts-rapporteur">
              <Select id="experts-rapporteur" value={panel.rapporteur_id} onChange={(e) => setPanel({ ...panel, rapporteur_id: e.target.value })}>
                <option value={0}>Premier expert actif</option>
                {experts.map((e) => <option key={e.id} value={e.id}>{e.name}{e.enabled ? '' : ' (désactivé)'}</option>)}
              </Select>
            </Field>
            {rapporteur && !rapporteur.enabled && <p className="mt-1.5 text-xs leading-relaxed text-warn-300">Cet expert est désactivé : le premier expert actif fera office de rapporteur.</p>}
          </div>
          <Field
            label="Tours de table ronde (max.)"
            htmlFor="experts-rounds"
            hint={`À chaque tour, chaque expert intervient une fois sur les points ouverts ; la table ronde s'arrête dès que tout a convergé. Avec ${panelSize} experts : jusqu'à ${panelSize * Number(panel.debate_rounds)} interventions.`}
          >
            <Select id="experts-rounds" value={panel.debate_rounds} onChange={(e) => setPanel({ ...panel, debate_rounds: e.target.value })}>
              {Array.from({ length: maxRounds }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} {n === 1 ? 'tour' : 'tours'}</option>)}
            </Select>
          </Field>
        </div>
        <div className="mt-5 flex items-start gap-3">
          <Checkbox
            switch
            id="experts-weighted-votes"
            className="mt-0.5"
            checked={panel.weighted_votes}
            onChange={(e) => setPanel({ ...panel, weighted_votes: e.target.checked })}
            aria-describedby="experts-weighted-votes-hint"
          />
          <div>
            <label htmlFor="experts-weighted-votes" className="cursor-pointer text-sm font-medium text-fg-body">Pondérer les votes par le bilan des experts</label>
            <p id="experts-weighted-votes-hint" className="mt-1 text-xs leading-relaxed text-fg-subtle">
              Chaque voix compte entre ×0,5 et ×1,5 selon les positions justes de l’expert à 20 séances (un expert sans historique compte ×1). L’unanimité et la décision par défaut ne changent pas ; les poids figurent en tête du rapport et dans le{' '}
              <Link to="/reports/scorecard" className="text-gold-300 underline-offset-2 hover:underline">bilan du panel</Link>. Les poids peuvent aussi suivre l’arène ou la calibration des experts (Trading › Arène des experts).
            </p>
          </div>
        </div>
        {panel.mandate && (
          <div className="mt-5 border-t border-line pt-5" data-testid="experts-mandate">
            <p className="text-sm font-medium text-fg-body">Votre mandat</p>
            <p className="mt-1 text-xs leading-relaxed text-fg-subtle">
              Donné au panel avant chaque débat : sa décision de liquidités reste dans cette fourchette, et une ligne détenue ne change que pour une raison précise et datée, pas par simple prudence.
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              <Field label="Liquidités minimum" htmlFor="experts-cash-min">
                <Input id="experts-cash-min" inputMode="decimal" value={panel.mandate.cash_min} onChange={(e) => setPanel({ ...panel, mandate: { ...panel.mandate, cash_min: e.target.value } })} trailing={<span className="pr-2 text-xs text-fg-subtle">%</span>} inputClassName="pr-10" />
              </Field>
              <Field label="Liquidités maximum" htmlFor="experts-cash-max">
                <Input id="experts-cash-max" inputMode="decimal" value={panel.mandate.cash_max} onChange={(e) => setPanel({ ...panel, mandate: { ...panel.mandate, cash_max: e.target.value } })} trailing={<span className="pr-2 text-xs text-fg-subtle">%</span>} inputClassName="pr-10" />
              </Field>
              <Field label="Horizon" htmlFor="experts-horizon">
                <Select id="experts-horizon" value={panel.mandate.horizon} onChange={(e) => setPanel({ ...panel, mandate: { ...panel.mandate, horizon: e.target.value } })}>
                  {HORIZONS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                </Select>
              </Field>
            </div>
          </div>
        )}
        <div className="mt-6 space-y-4 border-t border-line pt-5">
          {panelState.error && <Banner tone="loss" role="alert">{panelState.error}</Banner>}
          <div className="flex justify-end">
            <Button leadingIcon={Save} loading={panelState.saving} onClick={savePanel}>Enregistrer le panel</Button>
          </div>
        </div>
      </Card>
      {confirmDialog}
    </div>
  );
};

export default ExpertsSettings;
